import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography, Box, Tooltip, Button, CircularProgress, Chip
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../DataGrid/ControlledDataGrid';
import { jsonToCSV } from 'react-papaparse';
import { CSVDownloader } from 'react-papaparse';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs from 'dayjs';
import moment from 'moment';
import axios from 'axios';
import { APIURL } from '../../config';
import './ShiftReport.css';
import { getShiftStatus } from './shiftStatus';

const STATUS_MAP = {
    completed: { label: 'Completed', color: '#4caf50' },
    inProgress: { label: 'In Progress', color: '#2196f3' },
    upcoming: { label: 'Upcoming', color: '#ff9800' },
    missingCheckIn: { label: 'Missing Check-In', color: '#e64a19' },
    missed: { label: 'Missed', color: '#f44336' },
    unassigned: { label: 'Unassigned', color: '#9e9e9e' },
};

const columns = [
    {
        field: 'date',
        headerName: 'Date',
        flex: 0.8,
        renderCell: (params) => (
            <span>{params.value ? moment(params.value).utc().format('MM/DD/YYYY') : '-'}</span>
        ),
        sortComparator: (v1, v2) => moment(v1).diff(moment(v2)),
    },
    {
        field: 'shiftName',
        headerName: 'Shift Name',
        flex: 1.2,
        renderCell: (params) => (
            <Tooltip title={params.value || ''}>
                <span className="shift-report-cell-truncate">{params.value || '-'}</span>
            </Tooltip>
        ),
    },
    {
        field: 'customer',
        headerName: 'Customer',
        flex: 1.2,
        renderCell: (params) => (
            <Tooltip title={params.value || ''}>
                <span className="shift-report-cell-truncate">{params.value || '-'}</span>
            </Tooltip>
        ),
    },
    {
        field: 'taskType',
        headerName: 'Task Type',
        flex: 0.9,
    },
    {
        field: 'location',
        headerName: 'Location',
        flex: 1.2,
        renderCell: (params) => (
            <Tooltip title={params.value || ''}>
                <span className="shift-report-cell-truncate">{params.value || '-'}</span>
            </Tooltip>
        ),
    },
    {
        field: 'startTime',
        headerName: 'Start Time',
        flex: 0.7,
    },
    {
        field: 'endTime',
        headerName: 'End Time',
        flex: 0.7,
    },
    {
        field: 'duration',
        headerName: 'Hours',
        flex: 0.5,
        sortComparator: (v1, v2) => parseFloat(v1) - parseFloat(v2),
    },
    {
        field: 'employee',
        headerName: 'Employee',
        flex: 1.2,
        renderCell: (params) => (
            <Tooltip title={params.value || ''}>
                <span className="shift-report-cell-truncate">{params.value || 'Unassigned'}</span>
            </Tooltip>
        ),
    },
    {
        field: 'notes',
        headerName: 'Notes',
        flex: 1.5,
        renderCell: (params) => (
            <Tooltip title={params.value || ''}>
                <span className="shift-report-cell-truncate">{params.value || ''}</span>
            </Tooltip>
        ),
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        renderCell: (params) => {
            const info = STATUS_MAP[params.value] || STATUS_MAP.upcoming;
            return (
                <Chip
                    label={info.label}
                    size="small"
                    sx={{
                        backgroundColor: info.color,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                    }}
                />
            );
        },
    },
];

export default function ShiftReport(props) {
    const apiRef = useGridApiRef();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
    const [activeDateRange, setActiveDateRange] = useState([dayjs(), dayjs()]);

    const fetchShifts = useCallback(async (startDate, endDate) => {
        try {
            setLoading(true);
            const start = startDate.format('YYYY-MM-DD');
            const end = endDate.format('YYYY-MM-DD');

            // 1. Fetch schedules
            const schedulesRes = await axios.get(
                `${APIURL}/master_schedule/company/${props.comp_id}`,
                { params: { startDate: start, endDate: end } }
            );
            const schedules = schedulesRes.data || [];

            if (schedules.length === 0) {
                setRows([]);
                setLoading(false);
                return;
            }

            // 2. Fetch sessions for these schedules
            const scheduleIds = schedules.map(s => s.schedule_id);
            const sessionsRes = await axios.post(`${APIURL}/sessions/schedule`, { scheduleIds });
            const sessions = sessionsRes.data || [];

            // 3. Fetch employees, customers, resources in parallel
            const [employeesRes, customersRes, resourcesRes] = await Promise.all([
                axios.get(`${APIURL}/employees`, { params: { comp_id: props.comp_id } }),
                axios.get(`${APIURL}/customers`, { params: { com_id: props.comp_id } }),
                axios.get(`${APIURL}/resources/company/${props.comp_id}`),
            ]);
            const employees = employeesRes.data || [];
            const customers = customersRes.data || [];
            const resources = resourcesRes.data || [];

            const now = moment();

            // Build rows — one row per schedule
            const shiftRows = schedules.map((schedule) => {
                const scheduleSessions = sessions.filter(s => s.schedule_id === schedule.schedule_id);
                const empNames = (schedule.employee_ids || [])
                    .map(id => employees.find(e => e.id === id)?.employee_name)
                    .filter(Boolean)
                    .join(', ');
                const custNames = scheduleSessions
                    .map(s => customers.find(c => c.cus_id === s.customer_id)?.cus_name)
                    .filter(Boolean);
                const uniqueCust = [...new Set(custNames)].join(', ');
                const resource = resources.find(r => r.resource_id === schedule.resource_id);
                const shiftStart = moment(schedule.start_time);
                const shiftEnd = moment(schedule.end_time);
                const durationHrs = moment.duration(shiftEnd.diff(shiftStart)).asHours();
                const status = getShiftStatus(schedule, sessions, now);

                return {
                    id: schedule.schedule_id,
                    date: schedule.date,
                    shiftName: schedule.schedule_name || '-',
                    customer: uniqueCust || '-',
                    taskType: schedule.task_type || '-',
                    location: resource?.resource_name || '-',
                    startTime: shiftStart.format('h:mm A'),
                    endTime: shiftEnd.format('h:mm A'),
                    duration: durationHrs > 0 ? durationHrs.toFixed(2) : '-',
                    employee: empNames || 'Unassigned',
                    notes: schedule.notes || '',
                    status,
                };
            });

            setRows(shiftRows);
        } catch (error) {
            console.error('Error fetching shift report:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [props.comp_id]);

    useEffect(() => {
        if (activeDateRange[0] && activeDateRange[1]) {
            fetchShifts(activeDateRange[0], activeDateRange[1]);
        }
    }, [activeDateRange, fetchShifts]);

    const handleSearch = () => {
        if (dateRange[0] && dateRange[1]) {
            setActiveDateRange([dateRange[0], dateRange[1]]);
        }
    };

    const handlePrint = () => {
        let result = '<table>';
        result += '<tr>';
        columns.forEach(col => {
            result += `<th style="border-bottom:1px solid #000;padding:4px 8px">${col.headerName}</th>`;
        });
        result += '</tr>';
        rows.forEach(row => {
            result += '<tr>';
            columns.forEach(col => {
                let val = row[col.field] || '-';
                if (col.field === 'date' && val !== '-') {
                    val = moment(val).utc().format('MM/DD/YYYY');
                }
                if (col.field === 'status') {
                    val = STATUS_MAP[val]?.label || val;
                }
                result += `<td style="border-bottom:1px solid #ddd;padding:4px 8px">${val}</td>`;
            });
            result += '</tr>';
        });
        result += '</table>';
        const win = window.open('');
        win.document.write(result);
        win.print();
        win.close();
    };

    const csvData = jsonToCSV(rows.map(row => ({
        Date: row.date ? moment(row.date).utc().format('MM/DD/YYYY') : '-',
        'Shift Name': row.shiftName,
        Customer: row.customer,
        'Task Type': row.taskType,
        Location: row.location,
        'Start Time': row.startTime,
        'End Time': row.endTime,
        Hours: row.duration,
        Employee: row.employee,
        Notes: row.notes,
        Status: STATUS_MAP[row.status]?.label || row.status,
    })));

    return (
        <div className="shift-report-container">
            <div className="shift-report-title">
                Shift Report
            </div>

            <div className="shift-report-toolbar">
                <div className="shift-report-toolbar-left">
                    <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ start: 'From Date', end: 'To Date' }}>
                        <DateRangePicker
                            value={dateRange}
                            onChange={(newValue) => setDateRange(newValue)}
                            slotProps={{
                                fieldSeparator: { children: 'to' },
                                textField: { size: 'small' },
                            }}
                        />
                    </LocalizationProvider>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        sx={{ ml: 1 }}
                    >
                        Search
                    </Button>
                </div>
                <div className="shift-report-toolbar-right">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-info m-1"
                    >
                        <CSVDownloader data={csvData} filename="shift_report" bom={true}>
                            CSV
                        </CSVDownloader>
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-info m-1"
                        onClick={handlePrint}
                    >
                        PDF
                    </button>
                </div>
            </div>

            {loading && rows.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', margin: 10, marginTop: 50, width: '75vw' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <Box sx={{ width: '100%', height: 'calc(100vh - 320px)' }}>
                    <ControlledDataGrid
                        gridId={55}
                        user={props.user}
                        apiRef={apiRef}
                        rows={rows}
                        columns={columns}
                        initialState={{
                            sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
                        }}
                        disableColumnFilter
                        hideFooter={false}
                        filterMode="client"
                        style={{ color: '#000' }}
                        isRowSelectable={() => false}
                    />
                </Box>
            )}
        </div>
    );
}
